import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type {
  AssignmentPreview,
  GiftRecencyFlag,
  GiftSlotProduct,
  PreviewCreator,
  SkippedPreviewCreator,
  SkippedReason,
} from "@/types/mission-assign"

/**
 * OKS-935 / UX §5 — max characters of `variant_label` rendered INSIDE the chip.
 *
 * ⚠️ Clamp the DATUM, never the sentence. PO's original §5 put `truncate` on
 * the chip text; the copy is one sentence ending « …il y a {{count}} j », so
 * CSS truncation eats the DAY COUNT — the one number the brand decides on.
 * The clamp is applied BEFORE interpolation and the full, unclamped label is
 * carried in `title`, which §5 calls "the only place the whole label appears".
 *
 * ⚠️ Exported so tests bind to the constant. A retyped `24` keeps passing when
 * this changes and the test silently stops describing the code.
 */
export const RECENCY_LABEL_MAX = 24

/** Clamps a recency label to `RECENCY_LABEL_MAX`, marking elision with « … ». */
export function clampRecencyLabel(label: string): string {
  return label.length > RECENCY_LABEL_MAX
    ? `${label.slice(0, RECENCY_LABEL_MAX)}…`
    : label
}

/**
 * MissionAssignmentPreview — live preview of the assignment a confirm
 * would produce.
 *
 * Renders three sections (UX Spec §4):
 *   1. Count header + breakdown sub-line (`role="status"
 *      aria-live="polite"` so screen readers announce updates). Count
 *      animates with a 150 ms count-up (UX Spec §4 micro-interaction).
 *   2. New assignments list (`new_creators`).
 *   3. "Not in this batch" wrapper grouping the already-assigned and
 *      skipped sub-lists into a single muted section so the reader's
 *      eye treats both as "what won't change," not as separate noise.
 *
 * `loading` swaps the body for a Skeleton placeholder (UX Spec §3
 * Loading state — count skeleton + 5 skeleton rows).
 *
 * Long lists use a basic CSS-overflow scroll container — virtualization
 * is deferred until a brand actually has > ~100 active members; V1
 * Paalm is well under that.
 */
interface MissionAssignmentPreviewProps {
  preview: AssignmentPreview
  /** When true, renders the Skeleton loading state instead of preview body. */
  loading?: boolean
  testId?: string
}

const SKIPPED_ORDER: readonly SkippedReason[] = [
  "paused",
  "archived",
  "pending_charter",
  "mismatch_quarantine",
] as const

/**
 * How many products the breakdown lists before collapsing to "+N more".
 *
 * ⚠️ Three, because the readout exists to surface the CONSTRAINT and the
 * constraint is the scarcest few. A brand offering ten products does not need
 * ten lines under a badge; it needs to know which ones are about to run out.
 * The list is sorted scarcest-first, so the cap never hides the binding one.
 */
const GIFT_SLOTS_BREAKDOWN_CAP = 3

export function MissionAssignmentPreview({
  preview,
  loading = false,
  testId = "mission-assignment-preview",
}: MissionAssignmentPreviewProps) {
  const { t } = useTranslation()

  const newCount = preview.new_count
  const alreadyCount = preview.already_assigned_count
  const skippedTotal = SKIPPED_ORDER.reduce(
    (acc, key) => acc + (preview.skipped_status[key] ?? 0),
    0,
  )
  const animatedCount = useCountUp(newCount, 150)

  // OKS-494 — gift-slot guard surface. `gift_slots` is null on
  // non-gifting / unlimited missions (→ no indicator). `giftOver` drives
  // the destructive Badge + warning row + the disabled-confirm in
  // `Assign.tsx`. Derived from `preview` (not a separate prop) so it
  // recomputes on every `only:['preview']` partial reload.
  const giftSlots = preview.gift_slots ?? null
  const giftOver = giftSlots !== null && newCount > giftSlots.remaining

  /*
    OKS-935 — "already has it" flags, keyed by crew_membership_id so a row
    lookup is O(1) rather than a scan per creator.

    ⚠️ `null` (the guard cannot answer) and `[]` (it ran and flagged nobody)
    are DIFFERENT facts that render identically — silence. The Map is empty in
    both cases, which is correct for rendering and is exactly why the two are
    held apart in the payload and the specs rather than here.

    ⚠️ Nothing below gates Confirm. This feature WARNS, never blocks (AC8):
    `can_confirm`, `gift_slots` and the over-gift path are untouched.
  */
  const recencyByMembership = new Map<number, GiftRecencyFlag>(
    (preview.gift_recency ?? []).map((flag) => [flag.crew_membership_id, flag]),
  )
  const recencyCount = preview.gift_recency?.length ?? 0

  if (loading) {
    return (
      <div
        data-testid={testId}
        className="flex flex-col gap-4 rounded-lg border p-4"
      >
        <Skeleton
          className="h-6 w-48"
          data-testid="mission-assignment-preview-skeleton-count"
        />
        <div
          className="flex flex-col gap-2"
          data-testid="mission-assignment-preview-skeleton-rows"
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-4 rounded-lg border p-4"
    >
      <header className="flex flex-col gap-1">
        <h2
          role="status"
          aria-live="polite"
          className="text-foreground text-lg font-semibold"
          data-testid="mission-assignment-preview-heading"
        >
          {newCount === 0
            ? t("missions.assign.preview.empty")
            : t("missions.assign.preview.heading", { count: animatedCount })}
        </h2>
        <p
          className="text-muted-foreground text-xs"
          data-testid="mission-assignment-preview-breakdown"
        >
          {alreadyCount > 0 &&
            t("missions.assign.preview.already_assigned", {
              count: alreadyCount,
            })}
          {alreadyCount > 0 && skippedTotal > 0 && " · "}
          {skippedTotal > 0 &&
            t("missions.assign.preview.skipped_status_count", {
              count: skippedTotal,
            })}
        </p>
        {giftSlots && (
          <Badge
            variant={giftOver ? "destructive" : "secondary"}
            className="w-fit"
            data-testid="mission-assignment-preview-gift-slots"
          >
            {/* ⚠️ R14 — the total STAYS at N >= 2, it is only re-labelled.
                Dropping it in favour of the breakdown would remove the number
                the brand has been reading since OKS-494; keeping it unlabelled
                beside a per-product list reads as a contradiction ("12" above
                "1, 11"). The label is what makes it a sum rather than a rival
                claim. Single-product missions keep the shipped string byte for
                byte (UX §S9). */}
            {giftSlots.by_product.length >= 2
              ? t("missions.assign.preview.gift_slots_remaining_multi", {
                  remaining: giftSlots.remaining,
                  stock: giftSlots.stock,
                  count: giftSlots.by_product.length,
                })
              : t("missions.assign.preview.gift_slots_remaining", {
                  remaining: giftSlots.remaining,
                  stock: giftSlots.stock,
                })}
          </Badge>
        )}
        {/*
          OKS-935 S5 — summary line. Rendered only when the guard actually
          flagged someone: `[]` and `null` both render nothing, because silence
          IS the correct empty state here. "Nobody already has this product" is
          noise on the common case (UX §5).

          It sits inside the existing `role="status" aria-live="polite"` header
          region, so it is announced on partial reload without a second live
          region competing with the count.
        */}
        {recencyCount > 0 && (
          <p
            className="text-xs text-amber-700 dark:text-amber-400"
            data-testid="mission-assignment-preview-recency-summary"
          >
            {t("missions.assign.preview.recency.summary", {
              count: recencyCount,
            })}
          </p>
        )}
        <span className="sr-only" aria-live="polite">
          {t("missions.assign.preview.live_region")}
        </span>
      </header>

      {/* OKS-990 R14 — the per-product breakdown.
          ⚠️ N >= 2 ONLY. On a single-product mission the total already IS the
          distribution, so a breakdown would restate it; and S9 requires that
          preview to stay byte-identical to today. Absent, not empty. */}
      {giftSlots && giftSlots.by_product.length >= 2 && (
        <GiftSlotsBreakdown products={giftSlots.by_product} />
      )}

      {/* OKS-990 R14b — the mission-level overshoot, NAMED.
          ⚠️ NOT gated on N >= 2, and that is the whole point. `remaining` is
          clamped at 0, so an overshot mission renders "0 of 1" — true, and
          silent about the three that are actually out. On a SINGLE-product
          mission there is no breakdown to carry that fact, so without this line
          the overshoot is invisible on exactly the mission shape where nothing
          else can say it.
          ⚠️ ADDITIVE. `gift_slots_remaining` keeps its clamped rendering and
          `gift_slots_full` still fires at `remaining === 0`; this renders
          alongside them rather than replacing either (PRD R14b). A
          non-overshot mission is untouched. */}
      {giftSlots && giftSlots.consuming_count > giftSlots.stock && (
        <div
          className="text-destructive flex flex-col gap-0.5 text-xs"
          data-testid="mission-assignment-preview-gift-slots-overshoot"
        >
          <p>
            {t("missions.assign.preview.gift_slots_overshoot", {
              count: giftSlots.consuming_count - giftSlots.stock,
              stock: giftSlots.stock,
            })}
          </p>
          {/* ⚠️ The cause helper is NOT decoration and must not be dropped as
              redundant. Two causes reach this state — the brand lowered `stock`
              below what was granted, or creators voided possessions (R13,
              nothing the brand did). A bare "stock exceeded" on the surface the
              brand just clicked assign on reads as an accusation in the second
              case, which is the more common one. It names both, asserts
              neither. */}
          <p
            className="text-muted-foreground"
            data-testid="mission-assignment-preview-gift-slots-overshoot-cause"
          >
            {t("missions.assign.preview.gift_slots_overshoot_cause")}
          </p>
        </div>
      )}

      {giftSlots && (giftOver || giftSlots.remaining === 0) && (
        <p
          id="mission-assignment-preview-gift-slots-warning"
          role="alert"
          className="text-destructive text-xs"
          data-testid="mission-assignment-preview-gift-slots-warning"
        >
          {giftSlots.remaining === 0
            ? t("missions.assign.preview.gift_slots_full")
            : t("missions.assign.preview.gift_slots_over", {
                remaining: giftSlots.remaining,
                selected: newCount,
              })}
        </p>
      )}

      {newCount > 0 && (
        <PreviewSection
          title={t("missions.assign.preview.new_section")}
          creators={preview.new_creators}
          variant="new"
          recencyByMembership={recencyByMembership}
        />
      )}

      {(alreadyCount > 0 || skippedTotal > 0) && (
        <section
          className="flex flex-col gap-3"
          data-testid="mission-assignment-preview-not-in-batch"
        >
          <h3 className="text-foreground text-sm font-medium">
            {t("missions.assign.preview.not_in_batch_section")}
          </h3>

          {alreadyCount > 0 && (
            <PreviewSection
              title={t("missions.assign.preview.already_assigned_section")}
              creators={preview.already_assigned_creators}
              variant="muted"
            />
          )}

          {skippedTotal > 0 && (
            <SkippedSection
              title={t("missions.assign.preview.skipped_section")}
              creators={preview.skipped_creators}
            />
          )}
        </section>
      )}
    </div>
  )
}

interface PreviewSectionProps {
  title: string
  creators: PreviewCreator[]
  variant: "new" | "muted"
  /**
   * OKS-935 — passed ONLY by the `variant="new"` section (PRD D14). The muted
   * already-assigned section never receives it, so an already-assigned creator
   * structurally cannot render a chip: nothing is about to be shipped to them
   * by this action.
   */
  recencyByMembership?: Map<number, GiftRecencyFlag>
}

function PreviewSection({
  title,
  creators,
  variant,
  recencyByMembership,
}: PreviewSectionProps) {
  return (
    <section
      className="flex flex-col gap-2"
      data-testid={`mission-assignment-preview-section-${variant}`}
    >
      <h3 className="text-foreground text-sm font-medium">{title}</h3>
      <ul className="max-h-72 overflow-y-auto rounded border">
        {creators.map((creator) => (
          <CreatorRow
            key={creator.id}
            creator={creator}
            variant={variant}
            recency={recencyByMembership?.get(creator.id) ?? null}
          />
        ))}
      </ul>
    </section>
  )
}

interface SkippedSectionProps {
  title: string
  creators: SkippedPreviewCreator[]
}

function SkippedSection({ title, creators }: SkippedSectionProps) {
  const { t } = useTranslation()

  return (
    <section
      className="flex flex-col gap-2"
      data-testid="mission-assignment-preview-section-skipped"
    >
      <h3 className="text-foreground text-sm font-medium">{title}</h3>
      <ul className="max-h-72 overflow-y-auto rounded border">
        {creators.map((creator) => (
          <li
            key={creator.id}
            data-testid={`mission-assignment-preview-skipped-row-${creator.id}`}
            className="flex items-center justify-between gap-2 border-b px-3 py-2 text-sm opacity-60 last:border-b-0"
          >
            <span className="flex flex-col">
              <span className="text-foreground">
                {creator.name ?? creator.handle ?? `#${creator.id}`}
              </span>
              {creator.handle && (
                <span className="text-muted-foreground text-xs">
                  @{creator.handle}
                </span>
              )}
            </span>
            <Badge variant="outline">
              {t(`missions.assign.preview.skipped_reason.${creator.reason}`)}
            </Badge>
          </li>
        ))}
      </ul>
    </section>
  )
}

interface CreatorRowProps {
  creator: PreviewCreator
  variant: "new" | "muted"
  /** OKS-935 — this creator's recency flag, or null when unflagged. */
  recency?: GiftRecencyFlag | null
}

function CreatorRow({ creator, variant, recency = null }: CreatorRowProps) {
  const { t } = useTranslation()

  /*
    OKS-935 — the chip copy has four branches (UX §4), and `days_ago === 0` is
    a distinct one: "il y a 0 j" is wrong French for "today". The variant/
    product split is driven by `variant_label` being null — state A, where the
    product never had variants — not by any guess about the shipment.

    ⚠️ `count` is the i18next pluralisation driver. Passing it as `days` would
    render the `_other` form for 1 day.
  */
  /*
    Built TWICE over the same branches, differing only in the label:
    `recencyText` uses the CLAMPED label and is what renders; `recencyTitle`
    uses the FULL label and is what `title` carries (UX §5).

    ⚠️ The clamp is applied to the LABEL, before interpolation — never to the
    resulting sentence. Clamping the sentence removes whatever the translator
    put last, which is not a stable property across locales, and in FR that is
    the day count.
  */
  const recencySentence = (label: string | null) =>
    recency === null
      ? null
      : recency.days_ago === 0
        ? label !== null
          ? t("missions.assign.preview.recency.today_with_variant", { label })
          : t("missions.assign.preview.recency.today_product_only", {
              product: recency.product_name,
            })
        : label !== null
          ? t("missions.assign.preview.recency.with_variant", {
              label,
              count: recency.days_ago,
            })
          : t("missions.assign.preview.recency.product_only", {
              product: recency.product_name,
              count: recency.days_ago,
            })

  const recencyText = recencySentence(
    recency?.variant_label != null
      ? clampRecencyLabel(recency.variant_label)
      : null,
  )
  // Full label — the only place the whole thing appears (UX §5).
  const recencyTitle = recencySentence(recency?.variant_label ?? null)

  return (
    <li
      data-testid={`mission-assignment-preview-row-${creator.id}`}
      className={cn(
        "flex items-center justify-between gap-2 border-b px-3 py-2 text-sm last:border-b-0",
        variant === "muted" && "opacity-60",
      )}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-foreground truncate">
          {creator.name ?? creator.handle ?? `#${creator.id}`}
        </span>
        {creator.handle && (
          <span className="text-muted-foreground truncate text-xs">
            @{creator.handle}
          </span>
        )}
      </span>
      {/*
        ⚠️ UX §5 375px — with a chip AND tags the right cell overflows. Binding
        fix: stack them, cap the cell at 45% and truncate the chip text; the
        full string stays reachable via `title`.

        This is a LAYOUT assertion and Playwright owns it — jsdom does no
        layout, so `scrollWidth`/`clientWidth` are both 0 and a Vitest version
        of the overflow check passes vacuously whatever this markup does.
      */}
      {/*
        ⚠️ `!== null`, NOT `recencyText || …`. eslint pushes `||` → `??` on a
        nullable left operand, and that swap would be a BUG: `??` falls through
        only on null/undefined, so an EMPTY-string translation would return ""
        (falsy) and silently stop the TAGS from rendering. Same trap the
        `linkWouldOverwrite` comment in `mission-product-fieldset.tsx` documents.
        An explicit boolean says what is meant and satisfies the rule honestly
        rather than suppressing it.
      */}
      {(recencyText !== null || creator.tags.length > 0) && (
        <span className="flex max-w-[45%] shrink-0 flex-col items-end gap-1">
          {recencyText && (
            <Badge
              variant="outline"
              className="max-w-full border-amber-500/50 text-amber-700 dark:text-amber-400"
              /*
                ⚠️ `aria-label` IS required here, and the earlier comment
                saying otherwise was written for a design that no longer
                exists. It read: "The row truncates; the tooltip carries the
                full text. No aria-label — the text content IS the label."
                Both clauses are now false. The row does NOT truncate (UX §5
                withdrew it), and the text content is NOT the whole label —
                `clampRecencyLabel` cuts it mid-word past
                RECENCY_LABEL_MAX. A screen reader would announce
                "…Édition limitée collecto…", and `title` is not a reliable
                substitute: assistive tech announces it inconsistently and
                touch users never get it at all.

                So the accessible name carries the UNCLAMPED sentence while
                the visible text stays clamped. Colour is still not the only
                carrier — the string says it, in both channels.
              */
              aria-label={recencyTitle ?? undefined}
              title={recencyTitle ?? undefined}
              data-testid={`mission-assignment-preview-recency-${creator.id}`}
            >
              {/*
                ⚠️ NO `truncate` HERE — UX §5 withdrew it. The sentence ends
                « …il y a {{count}} j », so clipping the tail removes the day
                count. The DATUM is clamped instead (`clampRecencyLabel`), and
                `tests/playwright/oks935-gift-recency.spec.ts` reds if a
                truncating class reappears anywhere in this subtree.
              */}
              <span>{recencyText}</span>
            </Badge>
          )}
          {creator.tags.length > 0 && (
            <span className="text-muted-foreground truncate text-xs">
              {creator.tags.slice(0, 3).join(", ")}
            </span>
          )}
        </span>
      )}
    </li>
  )
}

// Tiny count-up — eases displayed value toward `target` over `durationMs`.
// Keeps the integer cadence (no fractional digits leak into the UI). On
// reduced-motion users this is barely perceptible at 150 ms; we skip the
// animation entirely if the diff is 0 or the duration is non-positive.
function useCountUp(target: number, durationMs: number): number {
  const [value, setValue] = useState(target)
  const fromRef = useRef(target)

  useEffect(() => {
    if (durationMs <= 0) return
    if (fromRef.current === target) return

    const start = performance.now()
    const from = fromRef.current
    let raf = 0

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const next = Math.round(from + (target - from) * t)
      setValue(next)
      if (t < 1) {
        raf = window.requestAnimationFrame(tick)
      } else {
        fromRef.current = target
      }
    }

    raf = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(raf)
  }, [target, durationMs])

  return durationMs <= 0 ? target : value
}

/**
 * OKS-990 R14 / UX §S9 — per-product capacity under the aggregate.
 *
 * # ⚠️ Why this exists at all
 *
 * The total is a SUM and is silent about distribution. With P0 stock 1 and P1
 * stock 11 it reads "12 remaining", `giftOver` is false at newCount 12, and the
 * brand gets a secondary-styled badge and NO alert — right before ten creators
 * are refused at request time. Nothing said was false; the preview promised
 * what the write refuses, which is `CapacityGuard`'s own named failure
 * relocated to the assign surface.
 *
 * # ⚠️ SCARCEST FIRST, not the brand's authoring order
 *
 * `(position, id)` is the authoring order and it is right everywhere the OFFER
 * is presented (PRD A5). This is not the offer — it is a capacity readout, and
 * the number that constrains the batch is the smallest one. Sorting by position
 * here would bury the product that is about to refuse people.
 *
 * # ⚠️ « épuisé » is a WORD and « dépassé de {n} » is a DIFFERENT FACT (R14b)
 *
 * `remaining` is clamped at 0, so an overshot product and an exactly-empty one
 * both read 0 — they are not the same thing. Overshoot is recoverable from
 * `consuming_count > stock` and is NAMED, because "out of stock" understates a
 * mission that has already granted more than it holds.
 */
function GiftSlotsBreakdown({ products }: { products: GiftSlotProduct[] }) {
  const { t } = useTranslation()

  // ⚠️ Copy before sort — `Array#sort` mutates, and this array is the prop.
  const ordered = [...products].sort((a, b) => a.remaining - b.remaining)
  const shown = ordered.slice(0, GIFT_SLOTS_BREAKDOWN_CAP)
  const rest = ordered.length - shown.length

  return (
    <div className="flex flex-col gap-1" data-testid="gift-slots-breakdown">
      <p className="text-muted-foreground text-xs font-medium">
        {t("missions.assign.preview.gift_slots_breakdown_label")}
      </p>
      <ul className="flex flex-col gap-0.5">
        {shown.map((product) => {
          const over = product.consuming_count - product.stock
          return (
            <li
              key={product.mission_product_id}
              className="text-muted-foreground text-xs"
              data-testid="gift-slots-product-row"
            >
              {over > 0
                ? t("missions.assign.preview.gift_slots_product_over", {
                    name: product.name,
                    count: over,
                  })
                : product.remaining === 0
                  ? t("missions.assign.preview.gift_slots_product_exhausted", {
                      name: product.name,
                    })
                  : t("missions.assign.preview.gift_slots_product", {
                      name: product.name,
                      remaining: product.remaining,
                    })}
            </li>
          )
        })}
      </ul>
      {rest > 0 && (
        <p
          className="text-muted-foreground text-xs"
          data-testid="gift-slots-breakdown-more"
        >
          {t("missions.assign.preview.gift_slots_breakdown_more", {
            count: rest,
          })}
        </p>
      )}
    </div>
  )
}
